import { MouseEvent, useCallback, useEffect, useState } from 'react';

import { CARD_IDS, CARD_TIMING, CAROUSEL } from '@/components/emotion-detector/constants';

// Create initial state outside component to avoid recreating on every render
const initialVisibleCards = new Set([CARD_IDS.INTRO]);
const initialDragStart = { x: 0, scrollLeft: 0 };

export function useEmotionCards() {
	// Progressive card revelation state
	const [visibleCards, setVisibleCards] = useState<Set<string>>(initialVisibleCards);
	// Version counter to help with stable dependencies
	const [visibleCardsVersion, setVisibleCardsVersion] = useState(0);

	// Mouse drag state for carousel
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState(initialDragStart);

	// Card navigation helpers
	const scrollToCard = (cardId: string) => {
		// Use a more robust selector and improved scrolling
		setTimeout(() => {
			const card = document.querySelector(`[data-card="${cardId}"]`);
			if (card && card.parentElement) {
				const container = card.closest('[data-carousel="container"]');
				if (container) {
					const cardRect = card.getBoundingClientRect();
					const containerRect = container.getBoundingClientRect();
					const scrollLeft =
						cardRect.left -
						containerRect.left +
						container.scrollLeft -
						containerRect.width / 2 +
						cardRect.width / 2;

					container.scrollTo({
						left: scrollLeft,
						behavior: 'smooth',
					});
				} else {
					// Fallback to original method
					card.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
						inline: 'center',
					});
				}
			}
		}, CARD_TIMING.SCROLL_DELAY);
	};

	const addVisibleCardAndScroll = useCallback((cardId: string) => {
		setVisibleCards(prev => {
			const newSet = new Set(prev);
			if (!newSet.has(cardId)) {
				newSet.add(cardId);
				// Increment version counter when Set changes
				setVisibleCardsVersion(v => v + 1);
				// Scroll to the new card after a delay to ensure it's rendered and animated
				setTimeout(() => scrollToCard(cardId), CARD_TIMING.CARD_REVEAL_DELAY);
			}
			return newSet;
		});
	}, []);

	const revealChatAndRecommendations = useCallback(() => {
		// Reveal recommendations first
		setVisibleCards(prev => {
			const newSet = new Set(prev);
			newSet.add(CARD_IDS.RECOMMENDATIONS);
			setVisibleCardsVersion(v => v + 1);
			return newSet;
		});

		// Then reveal chat card and scroll to it
		setTimeout(() => {
			setVisibleCards(prev => {
				const newSet = new Set(prev);
				newSet.add(CARD_IDS.CHAT);
				setVisibleCardsVersion(v => v + 1);
				return newSet;
			});
		}, CARD_TIMING.CHAT_REVEAL_DELAY);

		// Auto-scroll to chat card after both are revealed
		setTimeout(() => {
			scrollToCard(CARD_IDS.CHAT);
		}, CARD_TIMING.CHAT_SCROLL_DELAY);
	}, []);

	// Mouse drag handlers for carousel
	const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
		const container = e.currentTarget;
		setIsDragging(true);
		setDragStart({
			x: e.pageX - container.offsetLeft,
			scrollLeft: container.scrollLeft,
		});
	};

	const handleMouseLeave = () => {
		setIsDragging(false);
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
		if (!isDragging) return;
		e.preventDefault();
		const container = e.currentTarget;
		const x = e.pageX - container.offsetLeft;
		const walk = (x - dragStart.x) * CAROUSEL.DRAG_SENSITIVITY;
		container.scrollLeft = dragStart.scrollLeft - walk;
	};

	// Center the intro card on initial load
	useEffect(() => {
		// Wait for components to render, then center the intro card
		const timer = setTimeout(() => {
			scrollToCard(CARD_IDS.INTRO);
		}, CARD_TIMING.INITIAL_CENTER_DELAY);

		return () => clearTimeout(timer);
	}, []);

	const resetCardsAfterCapture = useCallback(() => {
		// Remove all cards after capture when starting fresh detection
		setVisibleCards(prev => {
			const newSet = new Set(prev);
			newSet.delete(CARD_IDS.SELECTION);
			newSet.delete(CARD_IDS.RECOMMENDATIONS);
			newSet.delete(CARD_IDS.CHAT);
			setVisibleCardsVersion(v => v + 1);
			return newSet;
		});
	}, []);

	return {
		// State
		visibleCards,
		visibleCardsVersion,
		isDragging,

		// Actions
		setVisibleCards,
		scrollToCard,
		addVisibleCardAndScroll,
		revealChatAndRecommendations,
		resetCardsAfterCapture,

		// Mouse handlers
		handleMouseDown,
		handleMouseLeave,
		handleMouseUp,
		handleMouseMove,
	};
}
